import { Link } from "react-router-dom";
import {
  Compass,
  Flame,
  Layers,
  Sprout,
  Target,
  Rocket,
  Blocks,
  ArrowUpRight,
} from "lucide-react";
import { visibleServices, serviceGroups } from "../data/services";

const iconMap = {
  "brand-strategy": Compass,
  "marketing-strategy": Target,
  "go-to-market": Rocket,
  creative: Layers,
  growth: Flame,
  "brand-build": Blocks,
  capital: Sprout,
};

function ServiceCard({ s }) {
  const Icon = iconMap[s.slug];
  const isComing = !!s.comingSoon;
  return (
    <Link
      to={`/services/${s.slug}`}
      data-testid={`service-card-${s.slug}`}
      className={`reveal group relative rounded-3xl p-8 md:p-10 overflow-hidden transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl ${
        isComing ? "border border-dashed border-[#1C1C19]/25" : ""
      }`}
      style={{ background: s.bg, color: s.fg }}
    >
      {isComing && (
        <span
          aria-hidden="true"
          className="absolute inset-0 bg-[#F7F5F0]/35 pointer-events-none"
        />
      )}

      <div className="relative flex justify-between items-start">
        <Icon size={32} strokeWidth={1.25} className={isComing ? "opacity-40" : ""} />
        {isComing ? (
          <span
            data-testid="capital-coming-soon-badge"
            className="text-[10px] font-bold uppercase tracking-[0.25em] bg-[#1C1C19] text-[#F7F5F0] rounded-full px-3 py-1.5"
          >
            Coming Soon
          </span>
        ) : (
          <span className="text-xs font-bold uppercase tracking-[0.25em] opacity-70">
            Practice
          </span>
        )}
      </div>

      <div className={`relative mt-14 md:mt-20 ${isComing ? "opacity-80" : ""}`}>
        <h3 className="font-display text-3xl md:text-5xl font-light tracking-tight leading-none">
          {s.title}
        </h3>
        <p className="mt-6 text-base md:text-lg font-light leading-relaxed max-w-md opacity-90">
          {s.summary.split(".")[0]}.
        </p>
      </div>

      <div className="relative mt-10 flex items-center justify-between">
        <div className="flex flex-wrap gap-2">
          {isComing ? (
            <span className="text-xs uppercase tracking-widest border border-current/60 rounded-full px-3 py-1.5 opacity-80">
              In development
            </span>
          ) : (
            s.offerings.slice(0, 4).map((o) => (
              <span
                key={o.title}
                className="text-xs uppercase tracking-widest border border-current/60 rounded-full px-3 py-1.5 opacity-90"
              >
                {o.title}
              </span>
            ))
          )}
        </div>
        <span className="ml-4 shrink-0 inline-flex items-center justify-center w-12 h-12 rounded-full border border-current/40 group-hover:bg-current group-hover:text-[#1C1C19] transition-all duration-500 group-hover:rotate-45">
          <ArrowUpRight size={18} />
        </span>
      </div>
    </Link>
  );
}

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="scroll-mt-24 bg-[#F7F5F0] py-24 md:py-40 border-t border-[#D8D4C7]"
    >
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-12 mb-20">
          <div className="md:col-span-3 reveal">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#5C5C58]">
              (03) · What we do
            </span>
            <div className="mt-6 w-12 h-px bg-[#1C1C19]" />
          </div>
          <h2 className="md:col-span-9 reveal font-display text-4xl sm:text-5xl lg:text-7xl font-light tracking-tight leading-[1.05] text-[#1C1C19]">
            Practices we run
            <br />
            <span className="italic">for the brands we serve.</span>
          </h2>
        </div>

        <div className="space-y-16 md:space-y-20">
          {serviceGroups.map((group) => {
            const cards = visibleServices.filter((s) => s.group === group);
            if (cards.length === 0) return null;
            return (
              <div key={group} data-testid={`service-group-${group.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="reveal mb-8 flex items-center gap-5">
                  <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#5C5C58]">
                    {group}
                  </span>
                  <div className="flex-1 h-px bg-[#D8D4C7]" />
                </div>
                <div className="grid md:grid-cols-3 gap-5 auto-rows-[minmax(260px,_auto)]">
                  {cards.map((s) => (
                    <ServiceCard key={s.slug} s={s} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
